import {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
} from 'react'
import { lockMainScroll, unlockMainScroll } from '@/utils/scrollLock'
import { FunctionComponentChildren } from '@/@types/react'

export type ModalSettings = {
  title?: string
  lockable?: boolean
  width?: number
  persistent?: boolean
}

export interface IModalContext {
  showModal(id: string, settings?: ModalSettings): void
  closeModal(): void
  open: string
  settings: ModalSettings
}

const ModalContext = createContext<IModalContext>(null)

export const ModalProvider: FunctionComponentChildren = ({ children }) => {
  const [open, setOpen] = useState<string>('')
  const [settings, setSettings] = useState<ModalSettings>({})

  const showModal = useCallback(
    (id: string, settings: ModalSettings = {}): void => {
      setSettings(settings)
      setOpen(id)
      lockMainScroll()
    },
    [],
  )

  const closeModal = useCallback((): void => {
    setOpen('')
    unlockMainScroll()
  }, [])

  const modalContextValue = useMemo(
    () => ({
      showModal,
      closeModal,
      open,
      settings,
    }),
    [showModal, closeModal, open, settings],
  )

  useEffect(() => {
    if (import.meta.env.VITE_APP_ENV_NAME !== 'PROD') {
      window.debugHeroModal = showModal
    }
  }, [showModal])

  return (
    <ModalContext.Provider value={modalContextValue}>
      {children}
    </ModalContext.Provider>
  )
}

export const useModalContext = (): IModalContext => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider')
  }
  return context
}
